import { Card, Text, Heading, Colors } from '@amelio/ui-library';

import type { ProficiencyLevel } from '@/types/competencies/ProficiencyLevel';

import { ProficiencyIcon } from '@/components/ProficiencyIcon/ProficiencyIcon';
import { resolveRankedProficiencyLevelColor } from '@/components/ProficiencyIcon/resolveProficiencyLevelColor';
import { getIconEntry } from '@/constants/proficiencyIconCatalog';
import { useAmelioTranslations } from '@/hooks';
import { getLocalizedText } from '@/utils/localization';
import { orderProficiencyLevels, orderProficiencyLevelsWorstFirst } from '@/utils/proficiencyLevel';

interface ProficiencyLevelsListProps {
    proficiencyLevels: ProficiencyLevel[];
}

/**
 * The company's mastery scale, read as a row under the assessment. A row is read left to right,
 * so it ends on the best level; the order is settled here rather than at each call site, so the
 * two documents that show this legend can never disagree.
 */
export const ProficiencyLevelsList = ({ proficiencyLevels }: ProficiencyLevelsListProps) => {
    const { t } = useAmelioTranslations();
    // Read left to right, the row ends on the best level; ranking keeps its own best-first order.
    const levels = orderProficiencyLevelsWorstFirst(proficiencyLevels);
    const orderedLevelIds = orderProficiencyLevels(proficiencyLevels).map((level) => level.id);

    return (
        <Card
            padding={'none'}
            className={'ml-2'}
        >
            <Heading
                level={5}
                color={Colors.NAVY}
                className={'text-caption! mb-[8px] font-semibold'}
            >
                {t('postReviewResults.competencies.proficiencyLevels')}
            </Heading>
            <div className={'flex flex-wrap gap-[12px]'}>
                {levels.map((level) => {
                    const iconEntry = getIconEntry(level.icon);

                    return (
                        <div
                            key={level.id}
                            className={'flex items-center gap-[4px]'}
                        >
                            <ProficiencyIcon
                                iconEntry={iconEntry}
                                iconUrl={level.iconUrl}
                                alt={getLocalizedText(level.label)}
                                color={resolveRankedProficiencyLevelColor({
                                    level,
                                    orderedLevelIds,
                                })}
                                className={'h-[16px] w-[16px]'}
                            />
                            <Text
                                size={'default'}
                                className={'text-caption!'}
                            >
                                {getLocalizedText(level.label)}
                            </Text>
                        </div>
                    );
                })}
            </div>
        </Card>
    );
};

ProficiencyLevelsList.displayName = 'ProficiencyLevelsList';
